import { apiClient, APIResponse } from './client';
import { Profile } from './profile';
import { encryptPassword } from '../utils/rsaEncryption';

export interface LoginData {
  emailid: string;
  passwordhash: string;
}

export interface SignUpData {
  name: string;
  emailid: string;
  password: string;
  profileType: string;
}

export interface ForgotPasswordRequest {
  emailid: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ForgotPasswordResponse {
  message: string;
  sent: boolean;
}

export interface ResetPasswordResponse {
  message: string;
  success: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface WorkgroupMembership {
  id: number;
  title: string;
  workgrouptype: string;
  /** Null only for a membership that hasn't been backfilled onto the dynamic roles table yet. */
  role: string | null;
}

export interface GitHubAuthResponse {
  accessToken: string;
  refreshToken: string;
  profile: Profile;
}

export interface AuthAPI {
  login(data: LoginData): Promise<APIResponse<AuthTokens>>;
  signUp(data: SignUpData): Promise<APIResponse<void>>;
  refreshToken(): Promise<APIResponse<AuthTokens>>;
  forgotPassword(data: ForgotPasswordRequest): Promise<APIResponse<ForgotPasswordResponse>>;
  resetPassword(data: ResetPasswordRequest): Promise<APIResponse<ResetPasswordResponse>>;
  verifyResetToken(token: string): Promise<APIResponse<{ valid: boolean; expired: boolean }>>;
  githubLogin(code: string): Promise<APIResponse<GitHubAuthResponse>>;
  getGitHubAuthUrl(): string;
  setToken(token: string | null): void;
  setTokens(tokens: AuthTokens | null): void;
  getStoredTokens(): AuthTokens | null;
  ensureValidToken(): Promise<void>;
  logout(): void;
  getWorkgroups(): Promise<APIResponse<WorkgroupMembership[]>>;
  switchWorkgroup(workgroupId: number): Promise<APIResponse<AuthTokens & { workgroupId: number }>>;
}

class AuthManager {
  private accessToken: string | null = null;
  private refreshTokenValue: string | null = null;
  private refreshPromise: Promise<APIResponse<AuthTokens>> | null = null;

  constructor() {
    this.loadTokensFromStorage();

    apiClient.setRefreshCallback(async () => {
      await this.refreshToken();
    });
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() >= exp;
    } catch (error) {
      return true;
    }
  }

  private isTokenExpiringSoon(token: string, bufferMinutes = 2): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      const bufferMs = bufferMinutes * 60 * 1000;
      return Date.now() >= (exp - bufferMs);
    } catch (error) {
      return true;
    }
  }

  async ensureValidToken(): Promise<void> {
    if (!this.accessToken || this.isTokenExpired(this.accessToken)) {
      await this.refreshToken();
    } else if (this.isTokenExpiringSoon(this.accessToken)) {
    }
  }

  private loadTokensFromStorage(): void {
    try {
      const tokens = localStorage.getItem('auth_tokens');
      if (tokens) {
        const parsed = JSON.parse(tokens);
        this.accessToken = parsed.accessToken;
        this.refreshTokenValue = parsed.refreshToken;
        apiClient.authToken = this.accessToken;
      }
    } catch (error) {
      this.clearTokens();
    }
  }

  private saveTokensToStorage(tokens: AuthTokens): void {
    try {
      localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    } catch (error) {
    }
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshTokenValue = null;
    apiClient.authToken = null;
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('token');
  }

  async login(data: LoginData): Promise<APIResponse<AuthTokens>> {
    const encryptedPassword = await encryptPassword(data.passwordhash);
    const response = await apiClient.post<AuthTokens>('/auth/login', {
      ...data,
      passwordhash: encryptedPassword,
    });
    if (response.data) {
      this.setTokens(response.data);
    }
    return response;
  }

  async signUp(data: SignUpData): Promise<APIResponse<void>> {
    try {
      const encryptedPassword = await encryptPassword(data.password);
      return apiClient.post<void>('/auth/register', {
        ...data,
        password: encryptedPassword,
      });
    } catch (error) {
      throw error;
    }
  }

  async refreshToken(): Promise<APIResponse<AuthTokens>> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = apiClient.post<AuthTokens>('/auth/refresh-token', {
      refreshToken: this.refreshTokenValue
    })
      .then((response) => {
        if (response.data) {
          this.setTokens(response.data);
        }
        return response;
      })
      .catch((error) => {

        this.logout();
        throw error;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  setToken(token: string | null): void {
    this.accessToken = token;
    apiClient.authToken = token;

    if (token && this.refreshTokenValue) {
      this.saveTokensToStorage({
        accessToken: token,
        refreshToken: this.refreshTokenValue
      });
    }
  }

  setTokens(tokens: AuthTokens | null): void {
    if (tokens) {
      this.accessToken = tokens.accessToken;
      this.refreshTokenValue = tokens.refreshToken;
      apiClient.authToken = tokens.accessToken;
      this.saveTokensToStorage(tokens);
    } else {
      this.clearTokens();
    }
  }

  getStoredTokens(): AuthTokens | null {
    if (this.accessToken && this.refreshTokenValue) {
      return {
        accessToken: this.accessToken,
        refreshToken: this.refreshTokenValue
      };
    }
    return null;
  }

  logout(): void {
    const accessToken = this.accessToken;
    const refreshToken = this.refreshTokenValue;

    if (accessToken) {
      apiClient.post<void>('/auth/logout', { refreshToken }).catch(() => {
        // Best-effort server-side revocation; local logout proceeds regardless.
      });
    }

    this.clearTokens();
    window.location.href = '/login';
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<APIResponse<ForgotPasswordResponse>> {
    const response = await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', data);
    return response;
  }

  async resetPassword(data: ResetPasswordRequest): Promise<APIResponse<ResetPasswordResponse>> {
    try {
      const response = await apiClient.post<ResetPasswordResponse>('/auth/reset-password', data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async verifyResetToken(token: string): Promise<APIResponse<{ valid: boolean; expired: boolean }>> {
    try {
      const response = await apiClient.get<{ valid: boolean; expired: boolean }>(`/auth/verify-reset-token/${token}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async githubLogin(code: string): Promise<APIResponse<GitHubAuthResponse>> {
    try {
      const response = await apiClient.post<GitHubAuthResponse>('/auth/github-callback', { code });
      if (response.data) {
        this.setTokens({
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken
        });
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  getGitHubAuthUrl(): string {
    const clientId = import.meta.env.VITE_GITHUB_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/github/callback`);
    const scope = 'repo,workflow,user:email';
    return `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;
  }

  async getWorkgroups(): Promise<APIResponse<WorkgroupMembership[]>> {
    return apiClient.get<WorkgroupMembership[]>('/auth/workgroups');
  }

  async switchWorkgroup(workgroupId: number): Promise<APIResponse<AuthTokens & { workgroupId: number }>> {
    const response = await apiClient.post<AuthTokens & { workgroupId: number }>('/auth/switch-workgroup', {
      workgroupId,
      refreshToken: this.refreshTokenValue,
    });
    if (response.data) {
      this.setTokens({ accessToken: response.data.accessToken, refreshToken: response.data.refreshToken });
    }
    return response;
  }
}

const authManager = new AuthManager();

export const authAPI: AuthAPI = {
  async login(data: LoginData) {
    return authManager.login(data);
  },
  async signUp(data: SignUpData) {
    return authManager.signUp(data);
  },
  async refreshToken() {
    return authManager.refreshToken();
  },
  async forgotPassword(data: ForgotPasswordRequest) {
    return authManager.forgotPassword(data);
  },
  async resetPassword(data: ResetPasswordRequest) {
    return authManager.resetPassword(data);
  },
  async verifyResetToken(token: string) {
    return authManager.verifyResetToken(token);
  },
  async githubLogin(code: string) {
    return authManager.githubLogin(code);
  },
  getGitHubAuthUrl() {
    return authManager.getGitHubAuthUrl();
  },
  setToken(token: string | null) {
    authManager.setToken(token);
  },
  setTokens(tokens: AuthTokens | null) {
    authManager.setTokens(tokens);
  },
  getStoredTokens() {
    return authManager.getStoredTokens();
  },
  async ensureValidToken() {
    return authManager.ensureValidToken();
  },
  logout() {
    authManager.logout();
  },
  async getWorkgroups() {
    return authManager.getWorkgroups();
  },
  async switchWorkgroup(workgroupId: number) {
    return authManager.switchWorkgroup(workgroupId);
  },
};
